<script setup lang="ts">
/**
 * ACRA Risk Assessment Form (CDD) — Word 원본 레이아웃.
 * Yes / No / NA 는 텍스트 입력이 아니라 ☐/☒ 체크 토글.
 */
import { onBeforeUnmount, onMounted, reactive, watch } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill } from './cdd/useCddFill';
import SgPreSigners from './SgPreSigners.vue';
import { SG_PRE_SIGNER_KEYS, useSgPreSigners } from './cdd/sgPreSignersContext';
import './allotDoc.css';

const fill = useCddFill();
const signersCtx = useSgPreSigners();

const SERVICES = [
  'Forming of corporations or other legal persons',
  'Acting, or arranging for another person to act as a director or secretary of a corporation; partner of a partnership; or a similar position in relation to other legal persons.',
  'Acting or arranging for another person to act as a shareholder on behalf of any corporation other than one whose securities are listed on a securities exchange.',
  'Providing a registered office, business address or correspondence or administrative address or other related services.',
  'Buying and selling real estate.',
  'Managing of client money, securities or other assets.',
  'Management of bank, savings or securities accounts.',
  'Organisation of contributions for the creation, operation or management of companies.',
  'Buying and selling of business entities.',
  'Statutory audit services.',
  'Providing other services (e.g. Annual Return filing, etc.)',
];

const B1 = [
  'Is this a new customer/client?',
  "Is the customer/client a public company listed on any stock exchange not subjected to disclosure requirements?",
  'Is the customer/client a legal person or an entity that can hold assets in its own name?',
  'Does the customer/client use nominee director(s) or shareholder(s)?',
  'Where applicable, do the nominee shareholders represent majority ownership?',
  'Does the ownership structure of the customer/client appear unusual or excessively complex given the nature of its business?',
  "Is the customer/client's business cash-intensive?",
  'Does the customer/client frequently make unaccounted cash transactions to similar recipient(s)?',
  'Do the proposed directors/partners/shareholders have prior criminal convictions involving fraud or dishonesty?',
  'Is any of the customer/client, beneficial owner or its agent a politically exposed person?',
  "Are the customer/client's company accounts outdated?",
  "Do the customer/client's shareholders and/or directors frequently change, and the changes are within reason?",
  'Is there any problem obtaining the required information in the relevant form?',
  'Can the information obtained be verified by independent and reliable sources?',
  'Is the customer/client a charitable or non-profit organisation that is not registered in Singapore?',
  'Does the client appear to be a shell company?',
  'Is the client in a high-risk industry?',
  'After further screening (Google, OFAC, third-party screening databases): are there adverse news or information arising? Or exceptions noted that cannot be disposed of satisfactorily?',
];

const B2 = [
  'Is the customer/client connected to or transacting with a country or territory that is identified as not having adequate AML/CFT measures?',
  'Is the customer/client connected to or transacting with a country or territory that has dealings with high-risk jurisdictions?',
  'Is the customer/client connected to or transacting with a country or territory that is sanctioned by a regulatory body, such as the United Nations (UN)?',
  'Is the customer/client connected to or transacting with a country or territory that is identified to be funding or supporting terrorist activities or that has designated terrorist organisations operating within their territories?',
  'Is the customer/client connected to or transacting with a country or territory in relation to which the FATF has called for countermeasures?',
];

const B3 = [
  'Has the customer/client given any instruction to perform a transaction (which may include cash) anonymously?',
  'Has the customer/client transferred any funds without the provision of underlying services or transactions?',
  'Are there unusual patterns of transactions that have no apparent economic purpose or cash payments that are large in amount, in which disbursement would have been normally made by other modes of payment (cheque, bank drafts etc.)?',
  'Are there unaccounted payments received from unknown or un-associated third parties for services and/or transactions provided by the customer/client?',
  'Is there instruction from the customer/client to incorporate shell companies with nominee shareholder(s) and/or director(s)?',
  'Does the customer/client purchase companies or business entities that have no obvious commercial purpose? (Multi-layer/multi-country structures; setting up Singapore entities with no obvious commercial purpose or personal/economic connection)',
  'Is this business relationship being established without any physical meeting?',
  'Are there any transactions that are inconsistent with the customer/client\'s profile?',
  'Are there any other unusual features of the transaction or services requested?',
];

type Yn = '' | 'yes' | 'no';
type YnNa = '' | 'yes' | 'no' | 'na';

const srvOn = reactive<Record<string, boolean>>({});
const b1Ans = reactive<Record<string, Yn>>({});
const b2Ans = reactive<Record<string, Yn>>({});
const b3Ans = reactive<Record<string, YnNa>>({});
const riskRating = reactive<{ value: '' | 'low' | 'medium' | 'high' }>({ value: '' });
const recommendation = reactive<{ value: '' | 'recommended' | 'not_recommended' }>({ value: '' });
const approval = reactive<{ value: '' | 'approved' | 'not_approved' }>({ value: '' });

const trackedKeys = [
  ...SERVICES.map((_, i) => `srv_a_${i + 1}`),
  ...B1.map((_, i) => `b1_q${i + 1}`),
  ...B2.map((_, i) => `b2_q${i + 1}`),
  ...B3.map((_, i) => `b3_q${i + 1}`),
  'customer_risk_rating',
  'recommendation',
  'approval',
];

onMounted(() => {
  trackedKeys.forEach((k) => fill.registerKey(k));
  // Word 용 signature 텍스트 (Signers UI 와 동기화) — 진행률에서는 선택 취급
  fill.registerKey('recommending_officer_signature');
  fill.registerKey('approving_officer_signature');
  // 미체크 서비스는 '' 가 아니라 진행률에서 제외(isProgressFilled) — 값은 no 로 정규화
  for (let i = 1; i <= SERVICES.length; i++) {
    const k = `srv_a_${i}`;
    if (!String(fill.values[k] || '').trim()) fill.setValue(k, 'no');
  }
  syncFromFill();
});
onBeforeUnmount(() => {
  trackedKeys.forEach((k) => fill.unregisterKey(k));
  fill.unregisterKey('recommending_officer_signature');
  fill.unregisterKey('approving_officer_signature');
});

/** Autofill / 외부 setValue → 로컬 체크 UI 동기화 */
function syncFromFill() {
  for (let i = 1; i <= SERVICES.length; i++) {
    const k = `srv_a_${i}`;
    const v = String(fill.values[k] || '').toLowerCase();
    srvOn[k] = v === 'yes' || v === 'true' || v === '1';
  }
  for (let i = 1; i <= B1.length; i++) {
    const k = `b1_q${i}`;
    const v = String(fill.values[k] || '').toLowerCase();
    b1Ans[k] = v === 'yes' || v === 'true' ? 'yes' : v === 'no' || v === 'false' ? 'no' : '';
  }
  for (let i = 1; i <= B2.length; i++) {
    const k = `b2_q${i}`;
    const v = String(fill.values[k] || '').toLowerCase();
    b2Ans[k] = v === 'yes' || v === 'true' ? 'yes' : v === 'no' || v === 'false' ? 'no' : '';
  }
  for (let i = 1; i <= B3.length; i++) {
    const k = `b3_q${i}`;
    const v = String(fill.values[k] || '').toLowerCase();
    b3Ans[k] =
      v === 'yes' || v === 'true' ? 'yes' : v === 'no' || v === 'false' ? 'no' : v === 'na' ? 'na' : '';
  }
  const rr = String(fill.values.customer_risk_rating || fill.values.risk_rating || '').toLowerCase();
  riskRating.value = rr === 'low' || rr === 'medium' || rr === 'high' ? (rr as any) : '';
  const rec = String(fill.values.recommendation || '').toLowerCase();
  recommendation.value =
    rec === 'recommended' || rec === 'not_recommended' ? (rec as any) : '';
  const ap = String(fill.values.approval || '').toLowerCase();
  approval.value = ap === 'approved' || ap === 'not_approved' ? (ap as any) : '';
  seedRiskSignersFromOfficerNames();
  syncSignatureTextFromSigners();
}

watch(
  () => trackedKeys.map((k) => fill.values[k]).join('|'),
  () => syncFromFill(),
);

watch(
  () =>
    [
      fill.values.recommending_officer_name,
      fill.values.recommending_officer_position,
      fill.values.approving_officer_name,
      fill.values.approving_officer_position,
    ].join('|'),
  () => {
    seedRiskSignersFromOfficerNames();
    syncSignatureTextFromSigners();
  },
);

watch(
  () =>
    [
      ...(signersCtx.signersOf(SG_PRE_SIGNER_KEYS.riskRecommend) || []).map((s) => s.name),
      ...(signersCtx.signersOf(SG_PRE_SIGNER_KEYS.riskApprove) || []).map((s) => s.name),
    ].join('|'),
  () => syncSignatureTextFromSigners(),
);

function toggleSrv(n: number) {
  const key = `srv_a_${n}`;
  srvOn[key] = !srvOn[key];
  fill.setValue(key, srvOn[key] ? 'yes' : 'no');
  if (!srvOn[key]) fill.setValue(`${key}_details`, '');
}

function setYn(map: Record<string, Yn>, key: string, val: Yn) {
  map[key] = map[key] === val ? '' : val;
  fill.setValue(key, map[key] === 'yes' ? 'yes' : map[key] === 'no' ? 'no' : '');
}

function setYnNa(map: Record<string, YnNa>, key: string, val: YnNa) {
  map[key] = map[key] === val ? '' : val;
  fill.setValue(key, map[key] || '');
}

function setOne(target: { value: string }, key: string, val: string) {
  target.value = target.value === val ? '' : val;
  fill.setValue(key, target.value);
}

/** Officer Name → Signers 위젯 시드 (비어 있을 때만) */
function seedRiskSignersFromOfficerNames() {
  signersCtx.seedFromValuesIfEmpty({
    recommending_officer_name: fill.values.recommending_officer_name,
    recommending_officer_position: fill.values.recommending_officer_position,
    approving_officer_name: fill.values.approving_officer_name,
    approving_officer_position: fill.values.approving_officer_position,
  });
}

/** Word `*_officer_signature` 텍스트 — Signers 첫 이름과 동기화 */
function syncSignatureTextFromSigners() {
  const rec = signersCtx.signersOf(SG_PRE_SIGNER_KEYS.riskRecommend);
  const app = signersCtx.signersOf(SG_PRE_SIGNER_KEYS.riskApprove);
  const recName = String(rec[0]?.name || '').trim();
  const appName = String(app[0]?.name || '').trim();
  if (recName) fill.setValue('recommending_officer_signature', recName);
  if (appName) fill.setValue('approving_officer_signature', appName);
}
</script>

<template>
  <div class="allot-doc risk-doc">
    <div class="doc-head">
      <div class="doc-lbl-title">ACRA RISK ASSESSMENT FORM</div>
      <div class="doc-meta">(Customer Due Diligence)</div>
      <div class="doc-meta" style="margin-top:6px">
        CSP: <CddVarField field-key="csp_name" label="CSP Name" />
      </div>
    </div>

    <p class="doc-sec-title">A. Nature of Services Required by Customer/Client</p>
    <table class="cdd-tbl risk-tbl">
      <colgroup>
        <col />
        <col class="yn" />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th>Services</th>
          <th class="yn-h">Select</th>
          <th>Details</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(label, i) in SERVICES" :key="'srv-' + i">
          <td>{{ i + 1 }}) {{ label }}</td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="toggleSrv(i + 1)">
              <span class="box">{{ srvOn[`srv_a_${i + 1}`] ? '✓' : '' }}</span>
            </span>
          </td>
          <td>
            <CddVarField
              v-if="srvOn[`srv_a_${i + 1}`]"
              :field-key="`srv_a_${i + 1}_details`"
              :label="`Details`"
            />
            <span v-else class="muted">—</span>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-p note">
      Section B — Risk Assessment of Customer/Client. The following checklists are examples of factors
      that you should consider when performing a risk assessment.
    </p>

    <p class="doc-sec-title">B1 — Customer's/Client's Risk Factors</p>
    <table class="cdd-tbl risk-tbl">
      <colgroup>
        <col />
        <col class="yn" />
        <col class="yn" />
      </colgroup>
      <thead>
        <tr>
          <th>Question</th>
          <th class="yn-h">Yes</th>
          <th class="yn-h">No</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(label, i) in B1" :key="'b1-' + i">
          <td>{{ i + 1 }}) {{ label }}</td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYn(b1Ans, `b1_q${i + 1}`, 'yes')">
              <span class="box">{{ b1Ans[`b1_q${i + 1}`] === 'yes' ? '✓' : '' }}</span>
            </span>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYn(b1Ans, `b1_q${i + 1}`, 'no')">
              <span class="box">{{ b1Ans[`b1_q${i + 1}`] === 'no' ? '✓' : '' }}</span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">B2 — Country/Territory Risk Factors</p>
    <p class="doc-p note">
      Consider: Customer/Client's nationality, Place of formation/incorporation, Residential address,
      Permanent address, Place of operation, Place where business is established; etc.
    </p>
    <table class="cdd-tbl risk-tbl">
      <colgroup>
        <col />
        <col class="yn" />
        <col class="yn" />
      </colgroup>
      <thead>
        <tr>
          <th>Question</th>
          <th class="yn-h">Yes</th>
          <th class="yn-h">No</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(label, i) in B2" :key="'b2-' + i">
          <td>{{ i + 1 }}) {{ label }}</td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYn(b2Ans, `b2_q${i + 1}`, 'yes')">
              <span class="box">{{ b2Ans[`b2_q${i + 1}`] === 'yes' ? '✓' : '' }}</span>
            </span>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYn(b2Ans, `b2_q${i + 1}`, 'no')">
              <span class="box">{{ b2Ans[`b2_q${i + 1}`] === 'no' ? '✓' : '' }}</span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">B3 — Services / Transactions Risk Factors</p>
    <table class="cdd-tbl risk-tbl">
      <colgroup>
        <col />
        <col class="yn" />
        <col class="yn" />
        <col class="yn" />
      </colgroup>
      <thead>
        <tr>
          <th>Question</th>
          <th class="yn-h">Yes</th>
          <th class="yn-h">No</th>
          <th class="yn-h">NA</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(label, i) in B3" :key="'b3-' + i">
          <td>{{ i + 1 }}) {{ label }}</td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYnNa(b3Ans, `b3_q${i + 1}`, 'yes')">
              <span class="box">{{ b3Ans[`b3_q${i + 1}`] === 'yes' ? '✓' : '' }}</span>
            </span>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYnNa(b3Ans, `b3_q${i + 1}`, 'no')">
              <span class="box">{{ b3Ans[`b3_q${i + 1}`] === 'no' ? '✓' : '' }}</span>
            </span>
          </td>
          <td class="yn-cell">
            <span class="cdd-cbx" @click="setYnNa(b3Ans, `b3_q${i + 1}`, 'na')">
              <span class="box">{{ b3Ans[`b3_q${i + 1}`] === 'na' ? '✓' : '' }}</span>
            </span>
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">C. Customer Risk Rating &amp; Conclusion</p>
    <table class="cdd-tbl risk-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell" style="width:36%">Customer Risk Rating</td>
          <td>
            <span class="cdd-cbx" @click="setOne(riskRating, 'customer_risk_rating', 'low')">
              <span class="box">{{ riskRating.value === 'low' ? '✓' : '' }}</span> Low
            </span>
            <span class="cdd-cbx" @click="setOne(riskRating, 'customer_risk_rating', 'medium')">
              <span class="box">{{ riskRating.value === 'medium' ? '✓' : '' }}</span> Medium
            </span>
            <span class="cdd-cbx" @click="setOne(riskRating, 'customer_risk_rating', 'high')">
              <span class="box">{{ riskRating.value === 'high' ? '✓' : '' }}</span> High
            </span>
          </td>
        </tr>
        <tr>
          <td class="cdd-lcell">Risk Rating Reasons</td>
          <td><CddVarField field-key="risk_rating_reasons" label="Risk Rating Reasons" /></td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">Recommendation for Acceptance of Customer</p>
    <table class="cdd-tbl risk-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell" style="width:36%">Recommendation</td>
          <td>
            <span class="cdd-cbx" @click="setOne(recommendation, 'recommendation', 'recommended')">
              <span class="box">{{ recommendation.value === 'recommended' ? '✓' : '' }}</span> Recommended
            </span>
            <span class="cdd-cbx" @click="setOne(recommendation, 'recommendation', 'not_recommended')">
              <span class="box">{{ recommendation.value === 'not_recommended' ? '✓' : '' }}</span> Not Recommended
            </span>
          </td>
        </tr>
        <tr>
          <td class="cdd-lcell">Name of Recommending Officer</td>
          <td><CddVarField field-key="recommending_officer_name" label="Recommending Officer Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Position / Title</td>
          <td><CddVarField field-key="recommending_officer_position" label="Position / Title" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Date</td>
          <td><CddVarField field-key="recommending_officer_date" label="Date" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Signature</td>
          <td class="risk-sig-cell">
            <SgPreSigners :var-key="SG_PRE_SIGNER_KEYS.riskRecommend" />
          </td>
        </tr>
      </tbody>
    </table>

    <p class="doc-sec-title">Approval for Acceptance of Customer</p>
    <table class="cdd-tbl risk-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell" style="width:36%">Approval</td>
          <td>
            <span class="cdd-cbx" @click="setOne(approval, 'approval', 'approved')">
              <span class="box">{{ approval.value === 'approved' ? '✓' : '' }}</span> Approved
            </span>
            <span class="cdd-cbx" @click="setOne(approval, 'approval', 'not_approved')">
              <span class="box">{{ approval.value === 'not_approved' ? '✓' : '' }}</span> Not Approved
            </span>
          </td>
        </tr>
        <tr>
          <td class="cdd-lcell">Name of Approving Officer</td>
          <td><CddVarField field-key="approving_officer_name" label="Approving Officer Name" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Position / Title</td>
          <td><CddVarField field-key="approving_officer_position" label="Position / Title" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Date</td>
          <td><CddVarField field-key="approving_officer_date" label="Date" /></td>
        </tr>
        <tr>
          <td class="cdd-lcell">Signature</td>
          <td class="risk-sig-cell">
            <SgPreSigners :var-key="SG_PRE_SIGNER_KEYS.riskApprove" />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.risk-doc :deep(.cdd-cbx) {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-right: 14px;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}
.risk-doc :deep(.cdd-cbx .box) {
  display: inline-flex;
  width: 13px;
  height: 13px;
  border: 1.5px solid #111;
  margin-right: 2px;
  vertical-align: middle;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  background: #fff;
}
.yn-cell { text-align: center; vertical-align: middle; width: 52px; }
.yn-cell :deep(.cdd-cbx) { margin-right: 0; justify-content: center; }
.muted { color: #9aa6bd; }
.note { font-size: 11.5px; color: #555; line-height: 1.45; margin: 8px 0 10px; }
.risk-tbl th { font-size: 11px; background: #f3f5f9; }
.risk-tbl td { font-size: 12px; line-height: 1.45; vertical-align: top; }
.risk-tbl thead th.yn-h { text-align: center; white-space: nowrap; width: 52px; }
.risk-sig-cell { padding-top: 6px; padding-bottom: 6px; }
.risk-sig-cell :deep(.sgf) { max-width: 420px; }
</style>
